import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { Alert, theme } from 'antd';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { filter, includes } from 'lodash';

import { NavBack } from '@/comp';
import { permissions } from '@/constants';
import { useStores } from '@/hooks/use-stores';
import IconChevronUp from '@/assets/icon-chevron-up.svg?react';
import IconChevronDown from '@/assets/icon-chevron-down.svg?react';
import IconExclamationMark from '@/assets/icon-exclamation-mark.svg?react';
import { EPButton, EPSelect, EPSelectOption } from '@/components';

import RulesTable from './RulesTable';
import { AlertRuleTypes, StationTier } from '@/constants/enums';

const { useToken } = theme;
type FilterParams = {
  alertType: string | null;
  deviceId: string | null;
  selectedRuleIds: string[];
};

const Rules = observer(({ rules, isSingleMode, alertRecord }) => {
  const { stationId, ruleType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { stationStore, rulesStore, permissionStore, devicesStore } = useStores();
  const isEPMonitorStation = stationStore.currentStation.stationTier === StationTier.Monitor;
  const [filterParams, setFilterParams] = useState<FilterParams>({
    alertType: isEPMonitorStation ? AlertRuleTypes.MANUFACTURER : isSingleMode ? null : AlertRuleTypes.CUSTOM,
    deviceId: null,
    selectedRuleIds: [],
  });
  const [filteredRules, setFilteredRules] = useState(rules);
  const [isAlertTypeSelectOpen, setIsAlertTypeSelectOpen] = useState(false);
  const [isDeviceSelectOpen, setIsDeviceSelectOpen] = useState(false);
  const { token } = useToken();
  const suffixIconAlertType = isAlertTypeSelectOpen ? <IconChevronUp /> : <IconChevronDown />;
  const suffixIconDevice = isDeviceSelectOpen ? <IconChevronUp /> : <IconChevronDown />;

  const getFilteredRules = () => {
    let result = rules;

    // Filter by device ID
    if (filterParams.deviceId) {
      const filteredByDeviceIds = [];

      rules.forEach(rule => {
        // Custom alert rule
        if (rule.conditions?.conditions) {
          rule.conditions?.conditions.forEach(condition => {
            const devicesIds = condition.devicesIds;
            const parentsDevicesIds = condition.devicesIds?.map(id => devicesStore.getParentDeviceById(id)?.id);
            const included = includes([...devicesIds, ...parentsDevicesIds], filterParams.deviceId);

            if (included) {
              filteredByDeviceIds.push(rule);
            }
          });
        }
        // Manufacturer alert rule
        else if (rule.deviceId === filterParams.deviceId) {
          filteredByDeviceIds.push(rule);
        }
      });

      result = filteredByDeviceIds;
    }

    // Filter by alert type
    if (filterParams.alertType) {
      result = filter(result, r => r.alertType === filterParams.alertType);
    }

    // Filter by selected rule IDs
    if (filterParams.selectedRuleIds && filterParams.selectedRuleIds.length > 0) {
      result = filter(result, r => filterParams.selectedRuleIds.includes(r.id));
    }

    return result;
  };
  const onAlertTypeSelectDropdownVisibleChange = visibility => setIsAlertTypeSelectOpen(visibility);

  const onDeviceSelectDropdownVisibleChange = visibility => setIsDeviceSelectOpen(visibility);

  const onChangeFilterByAlertType = value => {
    const newRuleType = value.toLowerCase();
    navigate(`/home/station/${stationId}/rules/${newRuleType}`);
  };

  const onChangeFilterByDeviceId = value => {
    setFilterParams({
      ...filterParams,
      deviceId: value,
    });
  };

  const onChangeFilterByRule = selectedIds => {
    setFilterParams({
      ...filterParams,
      selectedRuleIds: selectedIds || [],
    });
  };

  const navigateToRulesPage = () => {
    navigate(`/home/station/${stationId}/rules`);
  };

  const singleRuleWarningMessage = () => {
    if (!isSingleMode || !alertRecord || rulesStore.isLoading) {
      return false;
    }

    let message = null;
    if (rules && rules.length > 0) {
      const ruleLastUpdate = moment(rules[0]?.lastUpdated).utc();
      const alertRecordOpeningTime = moment(alertRecord.openingTime).utc();
      if (ruleLastUpdate.isAfter(alertRecordOpeningTime)) {
        message =
          t('This alert rule has changed since the record you clicked on. The last change was on') +
          ' ' +
          ruleLastUpdate.format('MMM D, YYYY');
      }
    } else {
      message = t('This alert rule has been deleted');
    }

    return message;
  };

  useEffect(() => {
    const newFilteredRules = getFilteredRules();
    setFilteredRules([...newFilteredRules]);
  }, [rules, filterParams]);

  useEffect(() => {
    if (!ruleType) return;

    setFilterParams({
      ...filterParams,
      alertType: isEPMonitorStation ? AlertRuleTypes.MANUFACTURER : ruleType.toUpperCase(),
    });
  }, [ruleType]);

  const clearFilters = () => {
    const { deviceId, selectedRuleIds } = filterParams;
    if (isSingleMode) {
      navigateToRulesPage();
    }
    if (deviceId === null && selectedRuleIds.length === 0) return;

    setFilterParams({
      ...filterParams,
      deviceId: null,
      selectedRuleIds: [],
    });
  };

  return (
    <Container>
      {singleRuleWarningMessage() && (
        <Alert
          message={
            <span
              style={{
                color: token.colorBorder,
                fontSize: token.fontSizeHeading3,
              }}
            >
              {singleRuleWarningMessage()}
            </span>
          }
          showIcon
          icon={<IconExclamationMark />}
        />
      )}
      <div className="top">
        {location.state?.previous ? (
          <NavBack url={`/home/station/${stationId}/${location.state.previous}`} />
        ) : (
          <NavBack url={`/home/station/${stationId}`} />
        )}

        <div className="title">
          <span>{t('Alert rules management')}</span>
          {stationStore.currentStation.name && <span className="name"> | {stationStore.currentStation.name}</span>}
        </div>

        <div className="subtitle">{t('The logic triggering alerts')}</div>

        <div className="flex justify-between align-end">
          <div className="action-bar">
            {!isSingleMode && (
              <div className="filter-by-source">
                <span className="filter-by-title">{t('Filter by source')}</span>
                <EPSelect
                  value={filterParams.alertType}
                  suffixIcon={suffixIconAlertType}
                  disabled={isSingleMode}
                  onChange={onChangeFilterByAlertType}
                  onDropdownVisibleChange={onAlertTypeSelectDropdownVisibleChange}
                >
                  {!isEPMonitorStation && <EPSelectOption key={AlertRuleTypes.CUSTOM}>{t('Custom')}</EPSelectOption>}
                  <EPSelectOption key={AlertRuleTypes.MANUFACTURER}>{t('Manufacturer')}</EPSelectOption>
                </EPSelect>
              </div>
            )}

            {!isSingleMode && (
              <div className="filter-by-device">
                <span className="filter-by-title">{t('Filter by device')}</span>
                <EPSelect
                  value={filterParams.deviceId}
                  suffixIcon={suffixIconDevice}
                  disabled={
                    !devicesStore.devicesMetadataWithoutPipes ||
                    devicesStore.devicesMetadataWithoutPipes.length === 0 ||
                    isSingleMode
                  }
                  onChange={onChangeFilterByDeviceId}
                  onDropdownVisibleChange={onDeviceSelectDropdownVisibleChange}
                >
                  <EPSelectOption value={null} className="ellipsis" key={-1}>
                    {t('All')}
                  </EPSelectOption>
                  {devicesStore.devicesMetadataWithoutPipesAndChildDevices.map(({ id, name }) => (
                    <EPSelectOption className="ellipsis" key={id} value={id}>
                      {name}
                    </EPSelectOption>
                  ))}
                </EPSelect>
              </div>
            )}

            {
              <div className="filter-by-rule">
                <span className="filter-by-title">{t('Filter by rule')}</span>
                <EPSelect
                  value={filterParams.selectedRuleIds}
                  suffixIcon={suffixIconDevice}
                  mode="multiple"
                  onChange={onChangeFilterByRule}
                  placeholder={t('All rules')}
                >
                  {rules
                    .filter(rule => rule.alertType === filterParams.alertType)
                    .map(rule => (
                      <EPSelectOption value={rule.id} className="ellipsis" key={rule.id}>
                        {rule.title}
                      </EPSelectOption>
                    ))}
                </EPSelect>
              </div>
            }

            <div className="clear-filters">
              <span className="filter-by-title"></span>
              <EPButton onClick={clearFilters} type="link">
                {t('clear')}
              </EPButton>
            </div>
          </div>
          {!isEPMonitorStation && (
            <Link to={`/home/station/${stationId}/rules/new`}>
              <EPButton disabled={!permissionStore.isAuth(permissions.CREATE_CUSTOM_ALERT)}>
                {t('Create new rule')}
              </EPButton>
            </Link>
          )}
        </div>
      </div>

      <div className="bottom">
        {/*{memoizedTable}*/}
        <RulesTable rules={filteredRules} isSingleMode={isSingleMode} />
      </div>
    </Container>
  );
});

const Container = styled.div`
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    padding: ${({ theme: { token } }) => token.paddingXL}px;
    overflow-x: auto;

    button.action-button {
        &:hover:not(:disabled) {
            path {
                fill: ${({ theme: { token } }) => token.colorText};
            }
        }
    }

    .top {
        flex-grow: 0;
        flex-shrink: 0;

        .title {
            color: ${({ theme: { token } }) => token.colorText};
            font-size: ${({ theme: { token } }) => token.fontSizeHeading3}px;
            margin-top: ${({ theme: { token } }) => token.paddingMD}px;

            .name {
                color: ${({ theme: { token } }) => token.colorTextSecondary};
                font-size: ${({ theme: { token } }) => token.fontSizeHeading3}px;
            }
        }

        .subtitle {
            color: ${({ theme: { token } }) => token.colorTextSecondary};
            font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
            margin-top: ${({ theme: { token } }) => token.paddingLG}px;
        }

        .action-bar {
            display: flex;
            margin-top: ${({ theme: { token } }) => token.paddingMD}px;
        }

        .filter-by-device,
        .filter-by-source,
        .filter-by-rule,
        .clear-filters {
            display: flex;
            flex-direction: column;
            color: ${({ theme: { token } }) => token.colorTextTertiary};
            font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
            margin-right: ${({ theme: { token } }) => token.paddingMD}px;
            width: 200px;

            .filter-by-title {
                margin-bottom: ${({ theme: { token } }) => token.marginXS}px;
                height: 14px;
            }
        }

        .clear-filters {
            width: fit-content;
        }
    }
}

.bottom {
    height: 100%;
    min-width: 1200px;
    flex-grow: 1;
    overflow-y: hidden;
    margin: ${({ theme: { token } }) => `${token.marginXL}px 0px ${token.marginXS}px`};
}
`;

export default Rules;
